import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { AiTwotoneEdit } from 'react-icons/ai';
import { FiTrash, FiPlusCircle } from 'react-icons/fi';
import { NumericFormat } from 'react-number-format';

import { Button } from '../../components/button/Button';
import CustomTableSortLabel from '../../components/table/CustomTableSortLabel/CustomTableSortLabel';
import { OrdemItemEdit } from './OrderItemEdit';
import { BackdropCustom } from '../../components/backdrop/Backdrop';

import {
  TableBodyCard,
  TableCard,
  TableCardRow,
  TableItems,
  TableCellCustom,
  TableCardTitle,
  TableCardDescription,
  IconButtonAdd,
  TotalsContainer,
  TotalsValues,
  OrderItemListContainer,
  ButtonSaveContainer,
  ActionButtonGroup,
  StatusBadge,
  PriceDisplay,
  QuantityDisplay,
  EmptyState,
  EditableFieldDisplay,
  ProductDisplay,
  ProductDescriptionDisplay,
  DerivationDisplay,
  TableCardActionsDescription,
} from './OrderItemList.style';

import { IOrderItem } from '../../interfaces/IOrderItem';
import { IOrder } from '../../interfaces/IOrder';
import { IProduct } from '../../interfaces/IProduct';
import IOrderItemListProps from './interfaces/IOrderItemListProps';

import { useOrderItemService } from '../../services/useOrderItemService';
import { useOrderService } from '../../services/useOrderService';
import { useProductService } from '../../services/useProductService';
import { useToastr } from '../../hooks/useToastr';
import { useAuth } from '../../hooks/auth';
import SocketioService from '../../SocketIoService';
import { OrderItemUpdateValues } from './OrderItemUpdateValues';
import { IProductStockBalance } from '../../interfaces/IProductStockBalance';

interface IParams {
  itemsUpdated?(): void;
  recalculateOrder?(orderId: string): Promise<void>;
}

const initialItem: IOrderItem = {
  sequence: 0,
  quantity: 0,
  price: 0,
  freightValue: 0,
  discountFTZ: 0,
  IPI_Percent: 0,
  IPI_Value: 0,
  ICMSST_Value: 0,
  ICMS_Percent: 0,
  amount: 0,
  offerPercentage: 0,
  offerValue: 0,
  errorMessage: null,
  product: {
    sku: '',
    derivation: '',
  },
};

const OrderItemList = forwardRef<IOrderItemListProps, IParams>((props, ref) => {
  const [items, setItems] = useState<IOrderItem[]>([]);
  const [itemId, setItemId] = useState<string | null>(null);
  const [perPage] = useState(200);
  const [currentPage] = useState(1);
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
  const [orderField, setOrderField] = useState<string>('sequence');

  const [loading, setLoading] = useState(false);
  const [loadingSkus, setLoadingSkus] = useState(false);
  const insSavingData = useRef(false);

  const [skus, setSkus] = useState<string[]>([]);
  const [derivation, setDerivation] = useState<string>('');
  const [products, setProducts] = useState<IProduct[]>([]);
  const [quantity, setQuantity] = useState('');
  const [sku, setSku] = useState<string>('');
  const [offerPercentage, setOfferPercentage] = useState('');
  const [additionPercentage, setAdditionPercentage] = useState('');

  const [autocompleteIsOpen, setAutocompleteIsOpen] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState<boolean>(false);

  const [orderItem, setOrderItem] = useState<IOrderItem>();
  const [order, setOrder] = useState<IOrder | null>(null);

  const [dialogUpdateValuesOpen, setDialogUpdateValuesOpen] = useState(false);

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { state: user } = useAuth();
  const toastr = useToastr();

  const { listItemsByOrderId } = useOrderItemService();
  const orderItemService = useOrderItemService();
  const orderService = useOrderService();
  const productService = useProductService();
  const socketIoService = new SocketioService();

  const [isSteper] = useState(location.pathname.split('/').indexOf('steper') !== -1);
  const [orderId] = useState(params.id);

  useImperativeHandle(ref, () => ({
    listOrder: handleLoadOrder,
    async finish() {
      const thereAreItemsWithErrors = items.filter(item => item.status?.id !== 2)?.length !== 0;
      const thereAreItems = items?.length !== 0;

      if (thereAreItemsWithErrors) {
        toastr.error('Existem itens com erros de cálculo.');
        return;
      }
      if (!thereAreItems) {
        toastr.error('Não é possível finalizar pedido sem itens.');
        return;
      }
      navigate(`/order/${params.id}/edit`);
    },

    addItem() {
      handleToEdit(null);
    },

    listItems: handleListItems,
  }));

  const handleCloseDialog = () => {
    setOpenDialog(!openDialog);
    if (openDialog) {
      handleListItems();
      setItemId(null);
    }
  };

  const handleRemoveIconClicked = (id: string) => {
    setItemId(id);
    setIsRemoveDialogOpen(true);
  };

  const handleDenyToRemove = () => {
    setItemId(null);
    setIsRemoveDialogOpen(false);
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-br', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatCurrencyNumber = (value: number, precision: number | undefined): string => {
    return new Intl.NumberFormat('pt-br', { minimumFractionDigits: precision || 0 }).format(value);
  };

  const isEditionDisabled = () => {
    return order?.orderStatus?.id !== 1 || loading || Number(order?.orderNumber) > 0;
  };

  const clearNotSavedItem = () => {
    setItems(current => {
      return current.filter(i => !!i.id);
    });
    setSku('');
    setDerivation('');
    setQuantity('');
    setOfferPercentage('');
    setAdditionPercentage('');
  };

  const setNewItemRow = () => {
    setSku('');
    setQuantity('');
    setDerivation('');
    setOfferPercentage('');
    setAdditionPercentage('');

    setItems(currentItems => {
      const lastSequence = currentItems[currentItems.length - 1]?.sequence || 0;
      const newSequence = lastSequence + 1;

      const newItem = { ...initialItem, sequence: newSequence };
      const newItems = [...currentItems, newItem];

      setTimeout(() => {
        initialItem.isEditing = true;
        setOrderItem({ ...initialItem, sequence: newSequence });
        setEditableRow(newSequence);
        setTimeout(() => {
          const element = document.getElementById(`edt-sku-${newSequence}`);
          element?.focus();
        }, 100);
      }, 50);

      return newItems;
    });
  };

  const handleLoadOrder = async (): Promise<void> => {
    await orderService
      .listOrder(`${orderId}`, true)
      .then(order => {
        setOrder(order);
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRemoveItem = async (): Promise<void> => {
    setLoading(true);
    setIsRemoveDialogOpen(false);
    orderItemService
      .remove(`${itemId}`)
      .then(async () => {
        props.itemsUpdated?.();
        setIsRemoveDialogOpen(false);
        toastr.success('Item excluído com sucesso!');
        await handleListItems();
        await handleLoadOrder();
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleToEdit = async (id: string | null) => {
    setItemId(id);
    handleCloseDialog();
  };

  const handleListSkus = async (filter = '') => {
    const query = `perPage=1000&currentPage=1&orderBy=sku&orderDirection=ASC&sku=${filter}`;
    setLoadingSkus(true);
    await productService
      .listProductsSkus(query)
      .then(result => {
        setSkus(result.data);
      })
      .catch(error => {
        toastr.error(`Ocorreu um erro inesperado ao listar os produtos: ${error.message}`);
      })
      .finally(() => {
        setLoadingSkus(false);
      });
  };

  const handleListProducts = async () => {
    if (sku) {
      return await productService
        .listProductsBySku(`${sku}`)
        .then(result => {
          const prd = result?.map(r => {
            if (!r.derivation || r.derivation === ' ') {
              r.derivation = 'SEM DERIVAÇÃO';
            }
            return { ...r };
          });
          setProducts(prd);
          return prd;
        })
        .catch(error => {
          toastr.error(`Ocorreu um erro inesperado ao listar os produtos: ${error.message}`);
        });
    }
  };

  const handleSetDerivationBySku = async (sku: string) => {
    return await productService
      .listProductsBySku(`${sku}`)
      .then(result => {
        const prd = result?.map(r => {
          if (!r.derivation || r.derivation === ' ') {
            r.derivation = 'SEM DERIVAÇÃO';
          }
          return { ...r };
        });
        setProducts(prd);

        if (prd && prd.length === 1) {
          const product = prd[0];

          setDerivation(product.derivation ? product.derivation : 'SEM DERIVAÇÃO');

          if (orderItem) {
            handleTableChange({
              ...orderItem,
              product: {
                ...orderItem.product,
                derivation: product.derivation,
                description: product.description,
              },
            });

            if (quantity && parseFloat(quantity) > 0) {
              handleSaveOrderItem(`${product.id}`);
            }
          }
        }

        return prd;
      })
      .catch(error => {
        toastr.error(`Ocorreu um erro inesperado ao listar os produtos: ${error.message}`);
      });
  };

  const handleListItems = useCallback(async () => {
    if (params.id) {
      setLoading(true);
      await listItemsByOrderId(
        params.id,
        `perPage=${perPage}&currentPage=${currentPage}&orderBy=${orderField}&orderDirection=${orderDirection}`,
      )
        .then(response => {
          setItems(response.data);
          if (response?.data?.length === 0) {
            setItems([{ ...initialItem, sequence: 1 }]);
          }
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    }
  }, [params.id, initialItem, perPage, currentPage, orderField, orderDirection, listItemsByOrderId]);

  const setEditableRow = async (sequence: number) => {
    setAutocompleteIsOpen(false);
    setItems(current => {
      return current.map((item: IOrderItem): IOrderItem => {
        if (item.sequence === sequence) {
          setOrderItem(item);
          item.isEditing = true;
          if (item?.product?.sku) {
            setSku(`${item?.product?.sku}`);
            setQuantity(`${item?.quantity}`);
            setDerivation(`${item?.product?.derivation}`);
            setOfferPercentage(`${item.offerPercentage || '0'}`);
            setAdditionPercentage(`${item.additionPercentage || '0'}`);
          }
        } else {
          if (item.isEditing && !item.id && orderItem) {
            item = { ...orderItem };
          }
          item.isEditing = false;
        }
        return item;
      });
    });
  };

  const handleSavingData = async () => {
    if (
      !!sku &&
      !!derivation &&
      !!quantity &&
      quantity !== '0' &&
      (orderItem?.product?.sku !== sku ||
        orderItem?.quantity !== parseFloat(quantity) ||
        orderItem?.offerPercentage !== parseFloat(offerPercentage) ||
        orderItem?.additionPercentage !== parseFloat(additionPercentage))
    ) {
      await handleListProducts();
      const newProduct = products.find(p => {
        return (p.sku === sku && p.derivation === derivation) || p.derivation === 'SEM DERIVAÇÃO';
      });

      if (newProduct) {
        handleSaveOrderItem(`${newProduct.id}`);
      }
    }
  };

  const formatNumber = (value: number, precision: number | undefined): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: precision,
    }).format(value);
  };
  const handleCreateOrder = async (item: IOrderItem) => {
    setLoading(true);
    await orderItemService
      .create(item)
      .then(() => {
        toastr.success('Item adicionado com sucesso');
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdateOrder = async (item: IOrderItem) => {
    setLoading(true);
    await orderItemService
      .update(`${item.id}`, item)
      .then(() => {
        toastr.success('Item atualizado com sucesso');
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSaveOrderItem = async (productId: string) => {
    const item: IOrderItem = {
      id: orderItem?.id,
      orderId,
      sequence: orderItem?.sequence || 0,
      productId: productId,
      quantity: Number(quantity),
      price: orderItem?.price || 0,
      freightValue: orderItem?.freightValue || 0,
      discountFTZ: orderItem?.discountFTZ || 0,
      IPI_Percent: orderItem?.IPI_Percent || 0,
      IPI_Value: orderItem?.IPI_Value || 0,
      ICMSST_Value: orderItem?.ICMSST_Value || 0,
      ICMS_Percent: orderItem?.ICMS_Percent || 0,
      amount: orderItem?.amount || 0,
      offerPercentage: Number(offerPercentage) || 0,
      additionPercentage: Number(additionPercentage) || 0,
      offerValue: orderItem?.offerValue || 0,
      status: orderItem?.status || undefined,
      errorMessage: orderItem?.errorMessage || '',
    };

    if (orderItem?.id) {
      await handleUpdateOrder(item);
    } else {
      await handleCreateOrder(item);
    }

    await handleListItems();
    await props.itemsUpdated?.();
    if (!orderItem?.id) {
      setTimeout(() => {
        setNewItemRow();
      }, 200);
    }
  };

  const handleTableChange = (item: IOrderItem) => {
    setItems(current => {
      return current.map(i => {
        if (i.id === item.id) {
          i = { ...item };
          i.isEditing = true;
        }
        return i;
      });
    });
  };

  const handleElementClick = (evt: any, name: string) => {
    if (isEditionDisabled()) {
      return;
    }

    const sequence = parseInt(`${evt?.target?.id?.split(`${name}-`)[1]}`);
    evt?.stopPropagation();

    setEditableRow(sequence);

    setTimeout(() => {
      const element = document.getElementById(`edt-${evt?.target?.id}`);
      element?.focus();
    }, 100);
  };

  const handleElementKeyDown = async (evt: any, name: string, sequence: number) => {
    if (isEditionDisabled()) {
      return;
    }

    const greaterSequence = items.find(i => i.sequence > sequence)?.sequence;
    const smallerSequence = items
      .filter(i => i.sequence < sequence)
      .sort((a, b) => b.sequence - a.sequence)
      .find(() => true)?.sequence;

    if (autocompleteIsOpen) {
      return;
    }

    if (evt.ctrlKey && evt.key === 'Delete') {
      const id = items.find(i => i.sequence === sequence)?.id;
      if (id) {
        handleRemoveIconClicked(id);
      }
    }

    if (evt.altKey && evt.key === 'ArrowDown') {
      return;
    }

    if (evt.code === 'Enter' || evt.code === 'NumpadEnter') {
      if (!!sku && !!derivation && !!quantity && quantity !== '0') {
        if (insSavingData.current) {
          return;
        }
        insSavingData.current = true;
        evt.preventDefault();

        await handleSavingData();
        insSavingData.current = false;
      }
    }

    if (evt.code === 'Tab' && name === 'additionPercentage') {
      if (insSavingData.current) {
        return;
      }
      insSavingData.current = true;
      evt.preventDefault();

      await handleSavingData();

      const thereIsNewRow = items.filter(i => !i.id);

      if (thereIsNewRow?.length !== 0) {
        insSavingData.current = false;
        return;
      }

      if (!greaterSequence) {
        setNewItemRow();
        insSavingData.current = false;
        return;
      }
      setEditableRow(greaterSequence);
      setTimeout(() => {
        const element = document.getElementById(`edt-sku-${greaterSequence}`);
        element?.focus();
      }, 200);

      insSavingData.current = false;
      return;
    }

    if (evt.code === 'ArrowDown' && !evt.altKey) {
      if (name === 'quantity' || name === 'offerPercentage' || name === 'additionPercentage') {
        if (!!sku && !!derivation && !!quantity && Number(quantity) > 0) {
          if (insSavingData.current) {
            return;
          }
          insSavingData.current = true;
          evt.preventDefault();

          await handleSavingData();

          setTimeout(async () => {
            const thereIsNewRow = items.filter(i => !i.id);

            if (thereIsNewRow?.length !== 0) {
              insSavingData.current = false;
              return;
            }

            if (!greaterSequence) {
              setNewItemRow();
              insSavingData.current = false;
              return;
            }
            setEditableRow(greaterSequence);
            setTimeout(() => {
              const element = document.getElementById(`edt-${name}-${greaterSequence}`);
              element?.focus();
            }, 200);
          }, 100);
          insSavingData.current = false;
          return;
        }
      } else {
        if (!!sku && !!derivation && !!quantity && Number(quantity) > 0) {
          evt.preventDefault();

          await handleSavingData();

          return;
        }
      }

      const thereIsNewRow = items.filter(i => !i.id);

      if (thereIsNewRow?.length !== 0) {
        return;
      }

      if (!greaterSequence) {
        setNewItemRow();
        return;
      }
      setEditableRow(greaterSequence);
      setTimeout(() => {
        const element = document.getElementById(`edt-${name}-${greaterSequence}`);
        element?.focus();
      }, 200);
    }

    if (evt.code === 'Escape') {
      clearNotSavedItem();
      setItems(current => {
        return current.map(i => {
          return { ...i, isEditing: false };
        });
      });

      if (!items.find(i => i.sequence === sequence)?.id && smallerSequence) {
        setEditableRow(smallerSequence);
        setTimeout(() => {
          const element = document.getElementById(`edt-${name}-${smallerSequence}`);
          element?.focus();
        }, 100);
      }
      return;
    }
    if (evt.code === 'ArrowUp') {
      clearNotSavedItem();
      if (!smallerSequence) {
        return;
      }
      setEditableRow(smallerSequence);
      setTimeout(() => {
        const element = document.getElementById(`edt-${name}-${smallerSequence}`);
        element?.focus();
      }, 200);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (isEditionDisabled()) {
      return;
    }

    // if (event.code === 'KeyN' && event.altKey) {
    //   event.preventDefault();
    //   console.log(orderItem?.isEditing);
    //   if (orderItem?.isEditing) {
    //     handleToEdit(orderItem?.id || null);
    //   } else {
    //     handleToEdit(null);
    //   }
    //   return;
    // }

    if (event.code === 'Insert') {
      const itemsTmp = [...items];

      const thereIsNewRow = itemsTmp.filter(i => !i.id);

      if (thereIsNewRow?.length === 0) {
        setNewItemRow();
      }
    }
  };

  const handleChangeSku = (event: any, newValue: string, item: IOrderItem) => {
    event.stopPropagation();
    setDerivation('');
    setQuantity('');
    setOfferPercentage('');
    setAdditionPercentage('');
    handleTableChange({
      ...item,
      product: {
        ...item.product,
        sku: String(newValue),
        description: ' ',
        stockQuantity: 0,
        productStockBalances: item.product?.productStockBalances || [],
      },
      price: 0,
      freightValue: 0,
      discountFTZ: 0,
      IPI_Percent: 0,
      IPI_Value: 0,
      ICMSST_Value: 0,
      ICMS_Percent: 0,
      amount: 0,
      offerValue: 0,
      status: undefined,
      errorMessage: null,
    });
    setSku(String(newValue));

    handleSetDerivationBySku(String(newValue));
  };

  const handleChangeDerivation = (event: any, newValue: string, item: IOrderItem) => {
    event.stopPropagation();
    const newDerivation = newValue === 'SEM DERIVAÇÃO' ? ' ' : String(newValue);
    setDerivation(newDerivation);
    const newProduct = products.find(p => {
      return (p.sku === sku && p.derivation === newDerivation) || p.derivation === 'SEM DERIVAÇÃO';
    });

    handleTableChange({
      ...item,
      product: {
        ...item.product,
        derivation: String(newValue),
        description: newProduct?.description,
      },
    });

    if (sku && quantity && parseFloat(quantity) > 0) {
      if (newProduct) {
        handleSaveOrderItem(`${newProduct.id}`);
      }
    }
  };

  const selectAutocompleteFocusedOnTab = (event: React.KeyboardEvent, autocompleteIsOpen: boolean) => {
    if (autocompleteIsOpen && event.key === 'Tab') {
      const popup = document.querySelector('[role="listbox"]');
      const input = event.target as HTMLElement;
      const activedescendant = input.getAttribute('aria-activedescendant');
      let toClick: HTMLElement | null = null;
      if (activedescendant) {
        toClick = popup?.querySelector(`#${activedescendant}`) as HTMLElement;
      }
      if (!toClick) {
        toClick = popup?.querySelector('[data-focus="true"]') as HTMLElement;
      }
      if (!toClick) {
        toClick = popup?.querySelector('[aria-selected="true"]') as HTMLElement;
      }
      if (toClick) {
        toClick.click();
        event.preventDefault();
      }
    }
  };

  const returnStockQuantity = useCallback(
    (productStockBalances: IProductStockBalance[]): string => {
      const stocks = productStockBalances?.filter(
        stock => String(stock.branchOfficeId) === String(order?.branchOfficeId),
      );

      const stock = stocks.find(s => {
        if (order?.orderTypeId !== 9) {
          return s.storage !== 'E-COMMERCE';
        } else {
          return s.storage === 'E-COMMERCE';
        }
      });

      return stock ? formatCurrencyNumber(stock.quantity, 2) : '0,00';
    },
    [order],
  );

  useEffect(() => {
    if (items.length === 0) {
      setItems([{ ...initialItem, sequence: 1 }]);
    }
  }, [items?.length]);

  useEffect(() => {
    handleListItems();
    handleListSkus();

    socketIoService.connect(user);
    socketIoService.subscribeToCalculateItem(updatedItem => {
      handleLoadOrder();
      //props.itemsUpdated?.();
      setItems(state => {
        return state.map(item => {
          return item.id === updatedItem.id ? updatedItem : item;
        });
      });
    });
    return () => {
      socketIoService.disconnect();
    };
  }, []);

  useEffect(() => {
    handleLoadOrder();
  }, [orderId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isEditingItem = items.some(item => item.isEditing);
      const isClickingOnFormFields = (event.target as HTMLElement)?.closest(
        '.MuiAutocomplete-root, .MuiTextField-root',
      );

      if (!isEditingItem || !isClickingOnFormFields) {
        clearNotSavedItem();
      }

      handleElementClick(event, 'out');
    };
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [items]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [items]);

  return (
    <>
      {isSteper && (
        <ButtonSaveContainer>
          <Button variant="contained" onClick={() => handleToEdit(null)}>
            Adicionar Itens
          </Button>
        </ButtonSaveContainer>
      )}

      <OrderItemListContainer>
        <Dialog
          open={isRemoveDialogOpen}
          onClose={() => {
            setIsRemoveDialogOpen(!isRemoveDialogOpen);
          }}
          sx={{
            '& .MuiDialog-container': {
              width: '100vw',
              justifyContent: 'center',
              alignItems: 'center',
            },
          }}
        >
          <DialogContent style={{ padding: '24px' }}>
            <DialogContentText
              sx={{
                marginBottom: '16px',
                textAlign: 'center',
                fontSize: '1.1rem',
                color: '#495057',
              }}
            >
              <strong>Confirma a exclusão do item?</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button color="error" variant="contained" onClick={handleDenyToRemove} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={handleRemoveItem} autoFocus loading={loading} disabled={loading} variant="contained">
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>

        {orderId && (
          <OrdemItemEdit
            openDialog={openDialog}
            orderId={orderId}
            orderBranchOfficeId={order?.branchOfficeId || ''}
            id={itemId || undefined}
            handleClose={handleCloseDialog}
            itemsUpdated={props.itemsUpdated}
            handleListItems={handleListItems}
          />
        )}
        {loading && <BackdropCustom />}
        <IconButtonAdd onClick={() => handleToEdit(null)} disabled={isEditionDisabled()}>
          <FiPlusCircle size={50} />
        </IconButtonAdd>

        {props.recalculateOrder && !isEditionDisabled() && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
            <Button variant="contained" onClick={() => setDialogUpdateValuesOpen(true)} disabled={isEditionDisabled()}>
              % Valores
            </Button>
          </div>
        )}

        <TableItems>
          <TableHead>
            <TableRow
              sx={{
                '&:last-child td, &:last-child th': {
                  borderBottom: 1,
                  borderColor: '#E5E5E5',
                },
              }}
            >
              <TableCellCustom size="small" style={{ minWidth: '80px', textAlign: 'center' }}>
                Ações
              </TableCellCustom>

              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="sequence"
                label="Seq."
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                disableOrder
                setField={setOrderField}
                setDirection={setOrderDirection}
              />

              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="sku"
                label="Produto"
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
                disableOrder
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="derivation"
                label="Derivação"
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
                disableOrder
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="description"
                label="Descrição"
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
                disableOrder
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="quantity"
                label="Quantidade"
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
                disableOrder
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="offerPercentage"
                label="% Oferta"
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
                disableOrder
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="additionPercentage"
                label="% Acréscimo"
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
                disableOrder
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="price"
                label="Preço Unitário"
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
                disableOrder
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="price"
                label="Uni. c/ Imp."
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
                disableOrder
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="IPI_Value"
                label="IPI"
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
                disableOrder
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="ICMSST_Value"
                label="ICMS ST"
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                disableOrder
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="ICMSS_value"
                label="ICMS"
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                disableOrder
                setField={setOrderField}
                setDirection={setOrderDirection}
              />

              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="status"
                label="Situação"
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                disableOrder
                setField={setOrderField}
                setDirection={setOrderDirection}
              />

              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="amount"
                label="Valor Total"
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                disableOrder
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="stockQuantity"
                label="Estoque"
                handleRequest={handleListItems}
                orderDirection={orderDirection}
                disableOrder
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {items && items.length > 0 ? (
              items.map(item => {
                return (
                  <TableRow
                    key={item.id}
                    sx={{
                      '&:last-child td, &:last-child th': {
                        border: 0,
                      },
                    }}
                  >
                    <TableCellCustom size="small" style={{ textAlign: 'center' }} width={80}>
                      <ActionButtonGroup>
                        <Tooltip title="Deletar" placement="top">
                          <span>
                            <button
                              className="delete-btn"
                              disabled={isEditionDisabled() || !item.id || item.statusId === 1}
                              onClick={() => handleRemoveIconClicked(`${item.id}`)}
                            >
                              <FiTrash size={16} />
                            </button>
                          </span>
                        </Tooltip>

                        <Tooltip title="Editar" placement="top">
                          <span>
                            <button
                              className="edit-btn"
                              disabled={isEditionDisabled() || !item.id}
                              onClick={() => handleToEdit(`${item.id}`)}
                            >
                              <AiTwotoneEdit size={16} />
                            </button>
                          </span>
                        </Tooltip>
                      </ActionButtonGroup>
                    </TableCellCustom>

                    <TableCellCustom size="small">{item.sequence}</TableCellCustom>
                    <TableCellCustom
                      size="small"
                      sx={{ whiteSpace: 'nowrap' }}
                      id={`sku-${item.sequence}`}
                      onClick={evt => {
                        handleElementClick(evt, 'sku');
                      }}
                      onKeyDown={evt => handleElementKeyDown(evt, 'sku', item.sequence)}
                    >
                      {item.isEditing ? (
                        <Autocomplete
                          id={`edt-sku-${item.sequence}`}
                          options={skus}
                          onClick={evt => evt.stopPropagation()}
                          disableClearable={true}
                          value={sku}
                          onOpen={() => setAutocompleteIsOpen(true)}
                          onClose={() => setAutocompleteIsOpen(false)}
                          onChange={(e: any, newValue) => {
                            handleChangeSku(e, newValue, item);
                          }}
                          isOptionEqualToValue={(option, value) => {
                            return option === value || true;
                          }}
                          onKeyDown={event => {
                            selectAutocompleteFocusedOnTab(event, autocompleteIsOpen);
                          }}
                          componentsProps={{
                            paper: {
                              sx: {
                                minWidth: 200,
                                maxWidth: 'unset',
                                whiteSpace: 'nowrap',
                                overflowX: 'auto',
                              },
                            },
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              sx={{
                                minWidth: '120px',
                                '& .MuiInputBase-root': {
                                  height: '32px',
                                  fontSize: '11px',
                                },
                                '& .MuiInputLabel-root': {
                                  fontSize: '11px',
                                },
                              }}
                              size="small"
                              label="Produto"
                              onClick={evt => evt.stopPropagation()}
                              InputProps={{
                                ...params?.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingSkus ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params?.InputProps?.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      ) : (
                        <ProductDisplay
                          id={`sku-${item.sequence}`}
                          onClick={evt => handleElementClick(evt, 'sku')}
                          style={{ cursor: 'pointer' }}
                        >
                          {item?.product?.sku}
                        </ProductDisplay>
                      )}
                    </TableCellCustom>
                    <TableCellCustom
                      size="small"
                      sx={{ whiteSpace: 'nowrap' }}
                      id={`derivation-${item.sequence}`}
                      onClick={evt => handleElementClick(evt, 'derivation')}
                      onKeyDown={evt => handleElementKeyDown(evt, 'derivation', item.sequence)}
                    >
                      {item.isEditing ? (
                        <Autocomplete
                          id={`edt-derivation-${item.sequence}`}
                          options={products.map(p => p.derivation)}
                          onClick={evt => evt.stopPropagation()}
                          onClose={() => setAutocompleteIsOpen(false)}
                          onOpen={() => {
                            setAutocompleteIsOpen(true);
                          }}
                          onKeyDown={event => {
                            selectAutocompleteFocusedOnTab(event, autocompleteIsOpen);
                          }}
                          disableClearable={true}
                          value={derivation === ' ' ? 'SEM DERIVAÇÃO' : derivation}
                          onChange={(e: any, newValue) => {
                            handleChangeDerivation(e, newValue, item);
                          }}
                          isOptionEqualToValue={(option, value) => {
                            return option === value || true;
                          }}
                          onFocus={() => {
                            handleListProducts();
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              sx={{
                                minWidth: '100px',
                                '& .MuiInputBase-root': {
                                  height: '32px',
                                  fontSize: '11px',
                                },
                                '& .MuiInputLabel-root': {
                                  fontSize: '11px',
                                },
                              }}
                              size="small"
                              label="Derivação"
                              onClick={evt => evt.stopPropagation()}
                            />
                          )}
                        />
                      ) : (
                        <DerivationDisplay
                          id={`derivation-${item.sequence}`}
                          onClick={evt => handleElementClick(evt, 'derivation')}
                          style={{ cursor: 'pointer' }}
                        >
                          {item.product?.derivation}
                        </DerivationDisplay>
                      )}
                    </TableCellCustom>
                    <TableCellCustom size="small">
                      <Tooltip title={item.product?.description || ''} placement="top">
                        <ProductDescriptionDisplay>{item.product?.description}</ProductDescriptionDisplay>
                      </Tooltip>
                    </TableCellCustom>

                    <TableCellCustom
                      size="small"
                      width={70}
                      onClick={(evt: any) => handleElementClick(evt, 'quantity')}
                      onKeyDown={evt => handleElementKeyDown(evt, 'quantity', item.sequence)}
                      id={`quantity-${item.sequence}`}
                    >
                      {item.isEditing ? (
                        <NumericFormat
                          id={`edt-quantity-${item.sequence}`}
                          size="small"
                          customInput={TextField}
                          label=""
                          value={quantity}
                          decimalScale={item.product?.measurementUnit?.precision || 0}
                          decimalSeparator=","
                          thousandSeparator="."
                          fixedDecimalScale
                          valueIsNumericString
                          onClick={(evt: any) => evt.stopPropagation()}
                          onValueChange={v => {
                            setQuantity(v.value || '');
                            handleTableChange({ ...item, quantity: parseFloat(v.value) || 0 });
                          }}
                          sx={{
                            '& .MuiInputBase-root': {
                              height: '32px',
                              fontSize: '11px',
                            },
                          }}
                        />
                      ) : (
                        <QuantityDisplay
                          id={`quantity-${item.sequence}`}
                          onClick={evt => handleElementClick(evt, 'quantity')}
                          style={{ cursor: 'pointer' }}
                        >
                          {formatCurrencyNumber(item.quantity, item?.product?.measurementUnit?.precision)}
                        </QuantityDisplay>
                      )}
                    </TableCellCustom>
                    <TableCellCustom
                      sx={{ minWidth: '70px' }}
                      size="small"
                      onClick={(evt: any) => handleElementClick(evt, 'offerPercentage')}
                      onKeyDown={evt => handleElementKeyDown(evt, 'offerPercentage', item.sequence)}
                      id={`offerPercentage-${item.sequence}`}
                    >
                      {item.isEditing ? (
                        <NumericFormat
                          id={`edt-offerPercentage-${item.sequence}`}
                          size="small"
                          customInput={TextField}
                          label=""
                          value={offerPercentage}
                          decimalScale={4}
                          decimalSeparator=","
                          thousandSeparator="."
                          fixedDecimalScale
                          valueIsNumericString
                          onClick={(evt: any) => evt.stopPropagation()}
                          onValueChange={v => {
                            setOfferPercentage(v.value || '');
                            handleTableChange({ ...item, offerPercentage: parseFloat(v.value) || 0 });
                          }}
                          sx={{
                            '& .MuiInputBase-root': {
                              height: '32px',
                              fontSize: '11px',
                            },
                          }}
                        />
                      ) : (
                        <EditableFieldDisplay
                          id={`offerPercentage-${item.sequence}`}
                          onClick={evt => handleElementClick(evt, 'offerPercentage')}
                          style={{ cursor: 'pointer' }}
                        >
                          {formatCurrencyNumber(item.offerPercentage, 4)}
                        </EditableFieldDisplay>
                      )}
                    </TableCellCustom>
                    <TableCellCustom
                      size="small"
                      width={70}
                      onClick={(evt: any) => handleElementClick(evt, 'additionPercentage')}
                      onKeyDown={evt => handleElementKeyDown(evt, 'additionPercentage', item.sequence)}
                      id={`additionPercentage-${item.sequence}`}
                    >
                      {item.isEditing ? (
                        <NumericFormat
                          id={`edt-additionPercentage-${item.sequence}`}
                          size="small"
                          customInput={TextField}
                          label=""
                          value={additionPercentage}
                          decimalScale={4}
                          decimalSeparator=","
                          thousandSeparator="."
                          fixedDecimalScale
                          valueIsNumericString
                          onClick={(evt: any) => evt.stopPropagation()}
                          onValueChange={v => {
                            setAdditionPercentage(v.value || '');
                            handleTableChange({ ...item, additionPercentage: parseFloat(v.value) || 0 });
                          }}
                          sx={{
                            '& .MuiInputBase-root': {
                              height: '32px',
                              fontSize: '11px',
                            },
                          }}
                        />
                      ) : (
                        <EditableFieldDisplay
                          id={`additionPercentage-${item.sequence}`}
                          onClick={evt => handleElementClick(evt, 'additionPercentage')}
                          style={{ cursor: 'pointer' }}
                        >
                          {formatCurrencyNumber(item.additionPercentage || 0, 4)}
                        </EditableFieldDisplay>
                      )}
                    </TableCellCustom>

                    <TableCellCustom size="small">
                      <PriceDisplay>
                        {`R$ ${(item.price ? item.price : 0).toLocaleString('pt-BR', {
                          style: 'decimal',
                          minimumFractionDigits: 2,
                        })}`}
                      </PriceDisplay>
                    </TableCellCustom>

                    <TableCellCustom size="small">
                      <PriceDisplay>
                        {`R$ ${(item.amount && item.quantity !== 0 ? item.amount / item.quantity : 0).toLocaleString(
                          'pt-BR',
                          {
                            style: 'decimal',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}`}
                      </PriceDisplay>
                    </TableCellCustom>

                    <TableCellCustom size="small" sx={{ whiteSpace: 'nowrap' }}>
                      <PriceDisplay>
                        {`R$ ${(item.IPI_Value ? item.IPI_Value : 0).toLocaleString('pt-BR', {
                          style: 'decimal',
                          minimumFractionDigits: 2,
                        })}`}
                      </PriceDisplay>
                    </TableCellCustom>

                    <TableCellCustom size="small" sx={{ whiteSpace: 'nowrap' }}>
                      <PriceDisplay>
                        {`R$ ${(item.ICMSST_Value ? item.ICMSST_Value : 0).toLocaleString('pt-BR', {
                          style: 'decimal',
                          minimumFractionDigits: 2,
                        })}`}
                      </PriceDisplay>
                    </TableCellCustom>

                    <TableCellCustom size="small" sx={{ whiteSpace: 'nowrap' }}>
                      <PriceDisplay>
                        {`${formatNumber(
                          item.ICMS_CalculationBase ? item.ICMS_CalculationBase * (item.ICMS_Percent / 100) : 0,
                          2,
                        )}`}
                      </PriceDisplay>
                    </TableCellCustom>

                    <TableCellCustom size="small" statusid={item.status?.id}>
                      <Tooltip title={item.errorMessage} placement="top">
                        <StatusBadge statusId={item.status?.id}>{item.status?.description}</StatusBadge>
                      </Tooltip>
                    </TableCellCustom>

                    <TableCellCustom size="small" sx={{ whiteSpace: 'nowrap' }}>
                      <PriceDisplay>
                        {`R$ ${(item.amount ? item.amount : 0).toLocaleString('pt-BR', {
                          style: 'decimal',
                          minimumFractionDigits: 2,
                        })}`}
                      </PriceDisplay>
                    </TableCellCustom>
                    <TableCellCustom size="small" width={60}>
                      <QuantityDisplay>
                        {item.product?.productStockBalances
                          ? returnStockQuantity(item.product?.productStockBalances)
                          : '0,00'}
                      </QuantityDisplay>
                    </TableCellCustom>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCellCustom colSpan={15}>
                  <EmptyState>
                    <FiPlusCircle />
                    <h3>Nenhum item adicionado</h3>
                    <p>Clique no botão "Adicionar Itens" para começar a adicionar produtos ao pedido.</p>
                  </EmptyState>
                </TableCellCustom>
              </TableRow>
            )}
          </TableBody>
        </TableItems>
        <TableBodyCard>
          {items && items.length > 0 ? (
            items.map(item => {
              return (
                <TableCard key={item.id}>
                  <TableCardRow>
                    <TableCardTitle>Seq.</TableCardTitle>
                    <TableCardDescription>{item.sequence}</TableCardDescription>
                  </TableCardRow>
                  <TableCardRow>
                    <TableCardTitle>Descrição</TableCardTitle>
                    <Tooltip title={item.product?.description} placement="top">
                      <TableCardDescription>
                        <ProductDescriptionDisplay>{item.product?.description}</ProductDescriptionDisplay>
                      </TableCardDescription>
                    </Tooltip>
                  </TableCardRow>
                  <TableCardRow>
                    <TableCardTitle>Produto</TableCardTitle>
                    <TableCardDescription>
                      <ProductDisplay>
                        {`${item.product?.sku}  ${
                          item.product?.derivation && item.product?.derivation?.length >= 1
                            ? `- ${item.product?.derivation}`
                            : ''
                        } `}
                      </ProductDisplay>
                    </TableCardDescription>
                  </TableCardRow>
                  <TableCardRow>
                    <TableCardTitle>Qtd.</TableCardTitle>
                    <TableCardDescription>
                      <QuantityDisplay>{item.quantity}</QuantityDisplay>
                    </TableCardDescription>
                  </TableCardRow>
                  <TableCardRow>
                    <TableCardTitle>Pre. Uni.</TableCardTitle>
                    <TableCardDescription>
                      <PriceDisplay>
                        {`R$ ${(item.price ? item.price : 0).toLocaleString('pt-BR', {
                          style: 'decimal',
                          minimumFractionDigits: 2,
                        })}`}
                      </PriceDisplay>
                    </TableCardDescription>
                  </TableCardRow>
                  <TableCardRow>
                    <TableCardTitle>Uni. c/ Imp.</TableCardTitle>
                    <TableCardDescription>
                      <PriceDisplay>
                        {`R$ ${(item.amount && item.quantity !== 0 ? item.amount / item.quantity : 0).toLocaleString(
                          'pt-BR',
                          {
                            style: 'decimal',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}`}
                      </PriceDisplay>
                    </TableCardDescription>
                  </TableCardRow>
                  <TableCardRow>
                    <TableCardTitle>IPI</TableCardTitle>
                    <TableCardDescription>
                      <PriceDisplay>
                        {`R$ ${(item.IPI_Value ? item.IPI_Value : 0).toLocaleString('pt-BR', {
                          style: 'decimal',
                          minimumFractionDigits: 2,
                        })}`}
                      </PriceDisplay>
                    </TableCardDescription>
                  </TableCardRow>
                  <TableCardRow>
                    <TableCardTitle>ICMS ST</TableCardTitle>
                    <TableCardDescription>
                      <PriceDisplay>
                        {`R$ ${(item.ICMSST_Value ? item.ICMSST_Value : 0).toLocaleString('pt-BR', {
                          style: 'decimal',
                          minimumFractionDigits: 2,
                        })}`}
                      </PriceDisplay>
                    </TableCardDescription>
                  </TableCardRow>
                  <TableCardRow>
                    <TableCardTitle>Situação</TableCardTitle>
                    <TableCardDescription statusid={item.status?.id}>
                      <Tooltip title={item.errorMessage} placement="top" enterTouchDelay={0} leaveTouchDelay={10}>
                        <StatusBadge statusId={item.status?.id}>{item.status?.description}</StatusBadge>
                      </Tooltip>
                    </TableCardDescription>
                  </TableCardRow>
                  <TableCardRow>
                    <TableCardTitle>Valor Total</TableCardTitle>
                    <TableCardDescription>
                      <PriceDisplay>
                        {`R$ ${(item.amount ? item.amount : 0).toLocaleString('pt-BR', {
                          style: 'decimal',
                          minimumFractionDigits: 2,
                        })}`}
                      </PriceDisplay>
                    </TableCardDescription>
                  </TableCardRow>{' '}
                  <TableCardRow>
                    <TableCardTitle>Ações</TableCardTitle>
                    <TableCardActionsDescription>
                      <ActionButtonGroup>
                        <Tooltip title="Deletar" placement="top">
                          <span>
                            <button
                              className="delete-btn"
                              disabled={isEditionDisabled()}
                              onClick={() => handleRemoveIconClicked(`${item.id}`)}
                            >
                              <FiTrash size={16} />
                            </button>
                          </span>
                        </Tooltip>

                        <Tooltip title="Editar" placement="top">
                          <span>
                            <button
                              className="edit-btn"
                              disabled={isEditionDisabled()}
                              onClick={() => handleToEdit(`${item.id}`)}
                            >
                              <AiTwotoneEdit size={16} />
                            </button>
                          </span>
                        </Tooltip>
                      </ActionButtonGroup>
                    </TableCardActionsDescription>
                  </TableCardRow>
                </TableCard>
              );
            })
          ) : (
            <EmptyState>
              <FiPlusCircle />
              <h3>Nenhum item adicionado</h3>
              <p>Clique no botão flutuante para começar a adicionar produtos ao pedido.</p>
            </EmptyState>
          )}
        </TableBodyCard>
      </OrderItemListContainer>

      <TotalsContainer>
        <TotalsValues>
          <strong>Total Produtos:</strong>
          <div>{formatCurrency(order?.grossTotalValue || 0)}</div>
        </TotalsValues>

        <TotalsValues>
          <strong>IPI:</strong>
          <div>{formatCurrency(order?.IPI_TotalValue || 0)}</div>
        </TotalsValues>
        <TotalsValues>
          <strong>ICMS ST:</strong>
          <div>{formatCurrency(order?.ICMSST_TotalValue || 0)}</div>
        </TotalsValues>

        <TotalsValues>
          <strong>Desconto ZF:</strong>
          <div>{formatCurrency(order?.discountFTZTotalValue || 0)}</div>
        </TotalsValues>

        <TotalsValues>
          <strong>ICMS total:</strong>
          <div>{formatCurrency(order?.ICMS_TotalValue || 0)}</div>
        </TotalsValues>

        <TotalsValues>
          <strong>Total Pedido:</strong>
          <div>{formatCurrency(order?.totalValue || 0)}</div>
        </TotalsValues>
      </TotalsContainer>

      {orderId && props.recalculateOrder && (
        <OrderItemUpdateValues
          handleClose={() => setDialogUpdateValuesOpen(false)}
          openDialog={dialogUpdateValuesOpen}
          recalculateOrder={props.recalculateOrder}
          orderId={orderId}
        />
      )}
    </>
  );
});

export default OrderItemList;
